import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import {
  OrganisationProgressClass,
  OrganisationProgressItem,
  OrganisationProgressSummary,
  OrganisationStudentProgress,
  ProgressItemType,
} from '../../types';
import { formatTimeAgo } from '../../utils/time';
import Navbar from '../shared/Navbar';
import StudentProgressDetailModal, { ProgressDetail } from './StudentProgressDetailModal';

const statusLabel = (status: OrganisationStudentProgress['status']) => {
  if (status === 'completed') return 'Completed';
  if (status === 'started') return 'Started';
  return 'Not started';
};

const ClassProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ itemType: string; itemId: string }>();
  const itemType = params.itemType === 'lesson' || params.itemType === 'exam'
    ? params.itemType as ProgressItemType
    : null;
  const itemId = params.itemId || '';
  const [item, setItem] = useState<OrganisationProgressItem | null>(null);
  const [classes, setClasses] = useState<OrganisationProgressClass[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [students, setStudents] = useState<OrganisationStudentProgress[]>([]);
  const [summary, setSummary] = useState<OrganisationProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProgressDetail | null>(null);
  const [detailLoadingUserId, setDetailLoadingUserId] = useState<number | null>(null);

  useEffect(() => {
    if (!itemType || !itemId) {
      setError('Invalid progress page');
      setLoading(false);
      return;
    }
    const loadClasses = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getOrganisationProgressClasses(itemType, itemId);
        setItem(response.item);
        setClasses(response.classes);
        setSelectedClassId(response.classes[0]?.id ?? null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    void loadClasses();
  }, [itemId, itemType]);

  useEffect(() => {
    if (!itemType || !itemId || selectedClassId === null) {
      setStudents([]);
      setSummary(null);
      return;
    }
    const loadProgress = async () => {
      try {
        setProgressLoading(true);
        setError(null);
        const response = await api.getOrganisationClassProgress(
          itemType,
          itemId,
          selectedClassId,
        );
        setStudents(response.students);
        setSummary(response.summary);
      } catch (err: any) {
        setError(err?.message || 'Failed to load class progress');
      } finally {
        setProgressLoading(false);
      }
    };
    void loadProgress();
  }, [itemId, itemType, selectedClassId]);

  const selectedClass = useMemo(
    () => classes.find(value => value.id === selectedClassId) ?? null,
    [classes, selectedClassId],
  );

  const openStudentDetail = async (student: OrganisationStudentProgress) => {
    if (!itemType || !itemId || selectedClassId === null || student.status === 'not_started') return;
    try {
      setDetailLoadingUserId(student.user_id);
      setError(null);
      setDetail(await api.getOrganisationStudentProgressDetail(
        itemType,
        itemId,
        selectedClassId,
        student.user_id,
      ));
    } catch (err: any) {
      setError(err?.message || 'Failed to load student detail');
    } finally {
      setDetailLoadingUserId(null);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container class-progress-page">
          <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={() => navigate(-1)}>
            Back to course
          </button>
          <div className="page-intro">
            <span className="class-progress-eyebrow">{item?.type === 'exam' ? 'Exam progress' : 'Lesson progress'}</span>
            <h2>{item?.title || 'Student progress'}</h2>
            <p>Select a class to review each student’s latest activity.</p>
          </div>

          {error && <div className="org-error" role="alert">{error}</div>}
          {loading ? (
            <div className="loading">Loading progress…</div>
          ) : classes.length === 0 ? (
            <div className="ml-empty">You do not have an eligible class with access to this item.</div>
          ) : (
            <>
              <section className="tm-surface class-progress-toolbar">
                <label>
                  Class
                  <select
                    value={selectedClassId ?? ''}
                    onChange={event => setSelectedClassId(Number(event.target.value))}
                  >
                    {classes.map(value => (
                      <option key={value.id} value={value.id}>
                        {value.name} ({value.student_count} students)
                      </option>
                    ))}
                  </select>
                </label>
                {selectedClass && <span>Viewing {selectedClass.name}</span>}
              </section>

              {summary && (
                <section className="class-progress-summary" aria-label="Class progress summary">
                  <div><strong>{summary.student_count}</strong><span>Students</span></div>
                  <div><strong>{summary.started_count}</strong><span>Started</span></div>
                  <div><strong>{summary.completed_count}</strong><span>Completed</span></div>
                </section>
              )}

              {progressLoading ? (
                <div className="loading">Loading students…</div>
              ) : students.length === 0 ? (
                <div className="ml-empty">This class has no student members.</div>
              ) : (
                <section className="class-progress-list">
                  <div className="class-progress-list-header">
                    <span>Student</span><span>Status</span><span>Latest activity</span><span>Result</span>
                  </div>
                  {students.map(student => (
                    <button
                      key={student.user_id}
                      type="button"
                      className="class-progress-row"
                      onClick={() => void openStudentDetail(student)}
                      disabled={student.status === 'not_started' || detailLoadingUserId !== null}
                    >
                      <span className="class-progress-student">{student.email}</span>
                      <span className={`class-progress-status class-progress-status--${student.status}`}>
                        {statusLabel(student.status)}
                      </span>
                      <span>
                        {student.latest_activity_at
                          ? formatTimeAgo(student.latest_activity_at)
                          : 'No activity'}
                      </span>
                      <span>
                        {student.obtained_marks !== null && student.obtained_marks !== undefined
                          ? `${student.obtained_marks} / ${student.total_marks ?? '—'}`
                          : student.latest_attempt_state?.replace('_', ' ') || '—'}
                      </span>
                    </button>
                  ))}
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {detail && (
        <StudentProgressDetailModal
          detail={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

export default ClassProgressPage;
